import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { chatApi } from '@/api';

const DEFAULT_TOPIC_ITEM = {
  id: null,
  title: 'Общий',
  isDefault: true,
};

export const useChatTopics = ({
  chatId,
  isGroupChat,
  isChatAdmin,
} = {}) => {
  const [topics, setTopics] = useState([]);
  const [activeTopicId, setActiveTopicId] = useState(null);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  const loadTopics = useCallback(async () => {
    if (!chatId || !isGroupChat) {
      setTopics([]);
      return [];
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoadingTopics(true);

    try {
      const response = await chatApi.getChatTopics(chatId);
      const nextTopics = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      if (!isMountedRef.current || requestIdRef.current !== requestId) {
        return [];
      }

      setTopics(nextTopics);

      return nextTopics;
    } catch (error) {
      console.error('Failed to load chat topics:', error);

      if (isMountedRef.current && requestIdRef.current === requestId) {
        setTopics([]);
      }

      return [];
    } finally {
      if (isMountedRef.current && requestIdRef.current === requestId) {
        setIsLoadingTopics(false);
      }
    }
  }, [chatId, isGroupChat]);

  useEffect(() => {
    requestIdRef.current += 1;
    setActiveTopicId(null);

    if (!chatId || !isGroupChat) {
      setTopics([]);
      setIsLoadingTopics(false);
      return;
    }

    loadTopics();
  }, [chatId, isGroupChat, loadTopics]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, []);

  const preparedTopics = useMemo(() => {
    if (!topics.length) return [];

    return [DEFAULT_TOPIC_ITEM, ...topics];
  }, [topics]);

  const activeTopic = useMemo(() => {
    if (activeTopicId === null) {
      return preparedTopics.find((topic) => topic.isDefault) ?? null;
    }

    return topics.find((topic) => String(topic.id) === String(activeTopicId)) ?? null;
  }, [activeTopicId, preparedTopics, topics]);

  const selectTopic = useCallback((topicId = null) => {
    setActiveTopicId(topicId ?? null);
  }, []);

  const createTopic = useCallback(async (title) => {
    const preparedTitle = title?.trim();
    if (!preparedTitle || !chatId) return null;

    const response = await chatApi.saveChatTopic({
      chat_id: chatId,
      title: preparedTitle,
    });

    await loadTopics();

    const nextTopicId = response?.data?.data?.id ?? null;

    if (nextTopicId !== null && nextTopicId !== undefined) {
      setActiveTopicId(nextTopicId);
    }

    return response?.data?.data ?? null;
  }, [chatId, loadTopics]);

  const renameTopic = useCallback(async ({ id, title }) => {
    const preparedTitle = title?.trim();
    if (!id || !preparedTitle || !chatId) return null;

    const response = await chatApi.saveChatTopic({
      id,
      chat_id: chatId,
      title: preparedTitle,
    });

    await loadTopics();

    return response?.data?.data ?? null;
  }, [chatId, loadTopics]);

  const deleteTopic = useCallback(async (topicId) => {
    if (!topicId || !chatId) return false;

    await chatApi.deleteChatTopic({
      id: topicId,
      chat_id: chatId,
    });

    await loadTopics();

    setActiveTopicId((prevTopicId) => (
      String(prevTopicId) === String(topicId) ? null : prevTopicId
    ));

    return true;
  }, [chatId, loadTopics]);

  const isTopicsVisible = isGroupChat && (topics.length > 0 || isChatAdmin);

  const changeTopicMessagesCount = useCallback((topicId, delta = 1) => {
    if (topicId === null || topicId === undefined || !delta) return;

    setTopics((prevTopics) => prevTopics.map((topic) => (
      String(topic.id) === String(topicId)
        ? {
          ...topic,
          messagesCount: Math.max(0, Number(topic?.messagesCount ?? 0) + delta),
        }
        : topic
    )));
  }, []);

  const applyTopicUnreadMessages = useCallback((topicUnreadMessages = [], options = {}) => {
    const openedTopicId = options?.openedTopicId;
    const unreadMap = new Map(
      topicUnreadMessages.map((item) => [String(item?.topicId), item]),
    );

    setTopics((prevTopics) => prevTopics.map((topic) => {
      const unreadItem = unreadMap.get(String(topic.id));
      const nextUnreadCount = Number(unreadItem?.unreadCount ?? 0);
      const isOpenedTopic =
        openedTopicId !== null &&
        openedTopicId !== undefined &&
        String(topic.id) === String(openedTopicId);

      return {
        ...topic,
        unreadCount: isOpenedTopic ? 0 : nextUnreadCount,
        firstUnreadMessageId: unreadItem?.firstUnreadMessageId ?? null,
      };
    }));
  }, []);

  return {
    topics,
    preparedTopics,
    activeTopic,
    activeTopicId,
    isLoadingTopics,
    isTopicsVisible,
    loadTopics,
    selectTopic,
    setActiveTopicId,
    createTopic,
    renameTopic,
    deleteTopic,
    changeTopicMessagesCount,
    applyTopicUnreadMessages,
  };
};
